import React from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input as UiInput } from "@/components/ui/input";
import { Input } from "antd";
import { UseFormReturn } from "react-hook-form";
import { useFormFormatters } from "@/shared/hooks";
import { getMaxLength } from "@/utils/fieldLimits";
import { PipelineFieldRow, PIPELINE_INPUT_CLASS } from "@/components/form/pipelineFieldRow";
import { usePipelineForm } from "@/components/form/PipelineFormContext";
import { cn } from "@/lib/utils";

type FormatterType = 'cpf' | 'cpfCnpj' | 'telefone' | 'moeda' | 'texto' | 'cep';

interface FormattedTextFieldProps {
  form: UseFormReturn<any>;
  name: string;
  label: string;
  formatter: FormatterType;
  placeholder?: string;
  required?: boolean;
  className?: string;
  onBlur?: () => void;
  maxLength?: number;
  disabled?: boolean;
  variant?: 'default' | 'pipeline';
}

export function FormattedTextField({
  form,
  name,
  label,
  formatter,
  placeholder,
  required = false,
  className = "",
  onBlur,
  maxLength,
  disabled = false,
  variant,
}: FormattedTextFieldProps) {
  const { variant: contextVariant } = usePipelineForm();
  const resolvedVariant = variant ?? contextVariant;
  const isPipeline = resolvedVariant === 'pipeline';
  const { formatCPF, formatCpfCnpj, formatTelefone, formatMoeda, formatCEP } = useFormFormatters();

  // Obtém o limite do campo baseado no nome, se não foi fornecido explicitamente
  const fieldMaxLength = maxLength || getMaxLength(name as any);

  const shouldCleanValue = (type: FormatterType) => {
    return ['cpf', 'cpfCnpj', 'telefone', 'moeda'].includes(type);
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        // Estado local para controlar o valor de exibição
        const [displayValue, setDisplayValue] = React.useState("");

        // Função para obter o valor formatado baseado no tipo
        const getFormattedValue = (value: string | number) => {
          switch (formatter) {
            case 'cpf':
              return formatCPF(value as string);
            case 'cpfCnpj':
              return formatCpfCnpj(value as string);
            case 'telefone':
              return formatTelefone(value as string || "");
            case 'moeda':
              // Para moeda, se o valor for string, assume que é um número em centavos
              if (typeof value === 'string') {
                const numericValue = parseFloat(value) || 0;
                return formatMoeda(numericValue);
              }
              return formatMoeda(value as number || 0);
            case 'cep':
              return formatCEP(value as string);
            default:
              return value as string;
          }
        };

        // Atualiza o display quando o valor do campo muda
        React.useEffect(() => {
          if (formatter === 'moeda') {
            setDisplayValue(getFormattedValue(field.value || 0));
          } else if (field.value !== undefined && field.value !== null && field.value !== "") {
            setDisplayValue(getFormattedValue(field.value));
          } else {
            setDisplayValue("");
          }
        }, [field.value, formatter, formatCPF, formatCpfCnpj, formatTelefone, formatMoeda, formatCEP]);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          // Apenas restringir se for um dos tipos numéricos
          const typesToRestrict = ['cpf', 'cpfCnpj', 'telefone', 'moeda', 'cep'];
          if (!typesToRestrict.includes(formatter)) return;

          // Permite apenas números, backspace, delete, tab, escape, enter e setas
          const allowedKeys = [
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End'
          ];

          const isNumber = /^[0-9]$/.test(e.key);
          const isAllowedKey = allowedKeys.includes(e.key);
          const isShortcut = (e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase());

          if (!isNumber && !isAllowedKey && !isShortcut) {
            e.preventDefault();
          }
        };
        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const inputValue = e.target.value;

          if (formatter === 'moeda') {
            // Para moeda, remove todos os caracteres não numéricos
            const numericValue = inputValue.replace(/\D/g, "");

            if (numericValue === "") {
              setDisplayValue("");
              field.onChange("");
              return;
            }

            // Converte para centavos e depois para reais
            const cents = parseInt(numericValue, 10);
            const reais = cents / 100;

            // Formata para exibição
            const formattedValue = new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(reais).replace(/\s/g, " ");

            setDisplayValue(formattedValue);
            field.onChange(reais); // Armazena como número para bater com o schema
          } else if (formatter === 'cpf') {
            // Para CPF, permite colar valores formatados como "225.837.342-53"
            // Remove formatação, limita a 11 dígitos e aplica formatação padrão
            const cleanValue = inputValue.replace(/\D/g, "");

            if (cleanValue === "") {
              setDisplayValue("");
              field.onChange("");
              return;
            }

            // Limita a 11 dígitos
            const limitedValue = cleanValue.slice(0, 11);

            // Formata o CPF
            const formattedValue = formatCPF(limitedValue);
            setDisplayValue(formattedValue);
            field.onChange(limitedValue); // Armazena apenas os números
          } else if (formatter === 'cpfCnpj') {
            // Para CPF/CNPJ, permite colar valores formatados
            // Remove formatação, limita a 14 dígitos (CNPJ) e aplica formatação dinâmica
            const cleanValue = inputValue.replace(/\D/g, "");

            if (cleanValue === "") {
              setDisplayValue("");
              field.onChange("");
              return;
            }

            // Limita a 14 dígitos (CNPJ)
            const limitedValue = cleanValue.slice(0, 14);

            // Formata CPF ou CNPJ baseado no tamanho
            const formattedValue = formatCpfCnpj(limitedValue);
            setDisplayValue(formattedValue);
            field.onChange(limitedValue); // Armazena apenas os números
          } else {
            const value = shouldCleanValue(formatter)
              ? inputValue.replace(/\D/g, "")
              : inputValue;

            // Para campos de telefone, aplica o limite ao valor formatado
            if (formatter === 'telefone') {
              const formattedValue = formatTelefone(value || "");
              if (fieldMaxLength && formattedValue.length > fieldMaxLength) {
                // Se o valor formatado excede o limite, não atualiza
                return;
              }
              setDisplayValue(formattedValue);
              field.onChange(value);
            } else {
              // Para outros campos, aplica o limite ao valor limpo
              const limitedValue = fieldMaxLength && value.length > fieldMaxLength
                ? value.slice(0, fieldMaxLength)
                : value;
              setDisplayValue(limitedValue);
              field.onChange(limitedValue);
            }
          }
        };

        const errorMessage = form.formState.errors[name]?.message as string | undefined;

        if (isPipeline) {
          return (
            <FormItem className={cn('flex flex-col p-0 space-y-0', className)}>
              <PipelineFieldRow label={label} required={required} error={errorMessage}>
                <Input
                  placeholder={placeholder}
                  value={displayValue}
                  onChange={handleInputChange}
                  onBlur={() => {
                    field.onBlur();
                    onBlur?.();
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={disabled}
                  variant="borderless"
                  className={PIPELINE_INPUT_CLASS}
                  status={errorMessage ? 'error' : undefined}
                />
              </PipelineFieldRow>
            </FormItem>
          );
        }

        return (
          <FormItem className={className}>
            <FormLabel className="mb-2">{label}{required && "*"}</FormLabel>
            <FormControl>
              <UiInput
                placeholder={placeholder}
                value={displayValue}
                onChange={handleInputChange}
                onBlur={() => {
                  field.onBlur();
                  onBlur?.();
                }}
                maxLength={formatter === 'cpf' || formatter === 'cpfCnpj' || formatter === 'moeda' ? undefined : fieldMaxLength}
                onKeyDown={handleKeyDown}
                className={`h-10 ${disabled ? "bg-gray-100 dark:bg-gray-800" : ""}`}
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
} 